import React, { useState } from 'react';
import { Send, Phone, Mail, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Contact = () => {
  const { isArabic } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleNativeEmail = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`New Inquiry from ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n\n` +
      `Message:\n${formData.message}`
    );
    window.location.href = `mailto:sazzadafam@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <section id="contact" className="py-24 bg-white text-corp-blue">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16 ${isArabic ? 'lg:flex-row-reverse' : ''}`}>
          
          {/* Contact Info */}
          <div className={isArabic ? 'text-right' : 'text-left'}>
            <h2 className="text-4xl font-bold mb-6">
              {isArabic ? "تواصل معنا" : "Get In Touch"}
            </h2>
            <p className="text-gray-600 mb-10 text-lg leading-relaxed">
              {isArabic 
                ? "نحن هنا للإجابة على استفساراتكم وتطوير شراكاتنا في جميع أنحاء المملكة العربية السعودية."
                : "We are here to answer your inquiries and develop our partnerships across the Kingdom of Saudi Arabia."}
            </p>
            
            <div className="space-y-8">
              <div className={`flex items-center gap-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center text-saudi-green shadow-sm">
                  <Phone size={28} />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-bold tracking-widest">{isArabic ? "اتصل بنا" : "Call Us"}</p>
                  <p className="text-xl font-bold">+966 (0) 576 566 566</p>
                </div>
              </div>

              <div className={`flex items-center gap-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center text-saudi-green shadow-sm">
                  <Mail size={28} />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-bold tracking-widest">{isArabic ? "البريد الإلكتروني" : "Email"}</p>
                  <p className="text-xl font-bold">info@afamksa.com</p>
                </div>
              </div>

              <div className={`flex items-center gap-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center text-saudi-green shadow-sm">
                  <MapPin size={28} />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-bold tracking-widest">{isArabic ? "الموقع" : "Location"}</p>
                  <p className="text-xl font-bold">{isArabic ? "الرياض، المملكة العربية السعودية" : "Fatimah Al Zahraa, Jarir, Al Malaz, Riyadh, Saudi Arabia"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-50 p-8 md:p-12 rounded-[2.5rem] border border-slate-100 shadow-2xl relative overflow-hidden">
             <div className="absolute top-0 right-0 w-32 h-32 bg-saudi-green/5 rounded-full -mr-16 -mt-16"></div>
            
            <form onSubmit={handleNativeEmail} className="relative z-10 space-y-6">
              {/* Name Input */}
              <div className={isArabic ? 'text-right' : 'text-left'}>
                <label className="block text-sm font-bold mb-2 text-slate-700">{isArabic ? "الاسم الكامل" : "Full Name"}</label>
                <input 
                  required
                  type="text" 
                  className={`w-full px-5 py-4 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-saudi-green transition-all bg-white ${isArabic ? 'text-right' : 'text-left'}`}
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>

              {/* Email Input */}
              <div className={isArabic ? 'text-right' : 'text-left'}>
                <label className="block text-sm font-bold mb-2 text-slate-700">{isArabic ? "البريد الإلكتروني" : "Email Address"}</label>
                <input 
                  required
                  type="email" 
                  className={`w-full px-5 py-4 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-saudi-green transition-all bg-white ${isArabic ? 'text-right' : 'text-left'}`}
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>

              {/* Message Input */}
              <div className={isArabic ? 'text-right' : 'text-left'}>
                <label className="block text-sm font-bold mb-2 text-slate-700">{isArabic ? "الرسالة" : "Message"}</label>
                <textarea 
                  required
                  rows="4"
                  className={`w-full px-5 py-4 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-saudi-green transition-all bg-white ${isArabic ? 'text-right' : 'text-left'}`}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                ></textarea>
              </div>

              <button 
                type="submit"
                className="w-full bg-corp-blue text-white py-5 rounded-2xl font-bold hover:bg-green-800 shadow-lg shadow-corp-blue/20 transition-all flex items-center justify-center gap-3 group"
              >
                {isArabic ? "إرسال الرسالة" : "Send Message"}
                <Send size={20} className={`group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform ${isArabic ? 'rotate-180' : ''}`} />
              </button>
            </form>
          </div>
        </div>

        {/* Map Embed Section */}
        <div className="rounded-[3rem] overflow-hidden shadow-xl border-8 border-slate-50 h-[450px] relative">
          <iframe 
            title="AFAM Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d231904.34114421252!2d46.54130098939634!3d24.743890200889908!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e2f03890d489399%3A0xba974d56c0433e2!2sRiyadh%20Saudi%20Arabia!5e0!3m2!1sen!2ssa!4v1700000000000!5m2!1sen!2ssa" 
            width="100%" 
            height="100%" 
            style={{ border: 0 }} 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            className="grayscale hover:grayscale-0 transition-all duration-700"
          ></iframe>
        </div>
      </div>
    </section>
  );
};

export default Contact;

